import type { Checklist } from '@cognite/apm-client';
import type { DateRangeType } from '@cognite/apm-observation';
import dayjs from 'dayjs';

import type { SchedulerChecklist } from '../types';

export const isDateRangeFilterForScheduledChecklistsValid = (
  dateRange: DateRangeType
) => {
  const [startDate, endDate] = dateRange;

  if (!startDate || !endDate) return false;

  const today = dayjs().endOf('day');
  const maxDateRange = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  const isStartDateValid = startDate.isBefore(today) || startDate.isSame(today);
  const isDateRangeValid = endDate.diff(startDate) <= maxDateRange;

  return isStartDateValid && isDateRangeValid;
};

// If the end date is in the future, update it to today
export const getDateRangeFilterWithoutFutureEndDate = (
  dateRange: DateRangeType
): DateRangeType => {
  const [startDate, endDate] = dateRange;
  if (!startDate || !endDate) return [null, null];

  const today = dayjs().endOf('day');

  if (endDate.isAfter(today)) {
    return [startDate, dayjs(today)];
  }

  return dateRange;
};

export const schedulerChecklistToChecklist = (
  item: SchedulerChecklist
): Checklist => {
  if (!item.external_id && !item.externalId) {
    throw new Error('Missing externalId or external_id');
  }
  if (!item.source_id && !item.sourceId) {
    throw new Error('Missing sourceId or source_id');
  }
  if (!item.start_time && !item.startTime) {
    throw new Error('Missing startTime or start_time');
  }
  if (!item.end_time && !item.endTime) {
    throw new Error('Missing endTime or end_time');
  }

  return {
    externalId: item.externalId || item.external_id!,
    sourceId: item.sourceId || item.source_id!,
    startTime: item.startTime || item.start_time!,
    endTime: item.endTime || item.end_time!,
    // We do not use any of the other fields directly in the UI, but we need to keep them in the object for the API
    // to work at the moment. The API will be updated to use a new minimal type soon, so when that is fully rolled out
    // we can remove this.
    // FO-1044: Remove mapping of type from the api service
    ...item,
  };
};

export const checklistToSchedulerChecklist = (
  item: Checklist
): SchedulerChecklist => {
  if (!item.externalId) {
    throw new Error('Missing externalId');
  }
  if (!item.sourceId) {
    throw new Error('Missing sourceId');
  }
  if (!item.startTime) {
    throw new Error('Missing startTime');
  }
  if (!item.endTime) {
    throw new Error('Missing endTime');
  }

  if (!item.status) {
    throw new Error('Missing status');
  }

  return {
    external_id: item.externalId,
    source_id: item.sourceId,
    start_time: item.startTime,
    end_time: item.endTime,
    status: item.status,
    // We do not use any of the other fields directly in the UI, but we need to keep them in the object for the API
    // See comment in the schedulerChecklistToChecklist function
    // FO-1044: Remove mapping of type from the api service
    ...item,
  };
};
